import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserFamilyInfo, FAMILY_ROLES, updateUserPermissions } from '../services/familyService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const useFamilyRole = () => {
  const { currentUser } = useAuth();
  const [familyRole, setFamilyRole] = useState(null);
  const [familyInfo, setFamilyInfo] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setFamilyRole(null);
      setFamilyInfo(null);
      setPermissions(null);
      setLoading(false);
      return;
    }

    const fetchFamilyInfo = async () => {
      try {
        setLoading(true);
        const userFamilyInfo = await getUserFamilyInfo(currentUser.uid);
        
        if (userFamilyInfo) {
          setFamilyRole(userFamilyInfo.role);
          setFamilyInfo(userFamilyInfo);
          
          // Get user permissions from user document
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setPermissions(userData.permissions || {
              canViewAllEvents: false,
              canEditEvents: false
            });
          } else {
            // Fallback to default permissions (same for all users)
            setPermissions({
              canViewAllEvents: false,
              canEditEvents: false
            });
          }
        } else {
          setFamilyRole(null);
          setFamilyInfo(null);
          
          // For users without families, get permissions from user document or set defaults
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setPermissions(userData.permissions || {
              canViewAllEvents: true,
              canEditEvents: true
            });
          } else {
            // Default permissions for users without families
            setPermissions({
              canViewAllEvents: true,
              canEditEvents: true
            });
          }
        }
      } catch (error) {
        console.error('Error fetching family info:', error);
        setFamilyRole(null);
        setFamilyInfo(null);
        setPermissions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyInfo();
  }, [currentUser]);

  // Update permissions function
  const updatePermissions = async (newPermissions) => {
    if (!familyInfo?.familyId || !currentUser) {
      console.error('No family ID or current user found');
      return { error: 'No family found' };
    }

    try {
      const result = await updateUserPermissions(familyInfo.familyId, currentUser.uid, newPermissions);
      if (result.success) {
        setPermissions(prev => ({
          ...prev,
          ...newPermissions
        }));
      }
      return result;
    } catch (error) {
      console.error('Error updating permissions:', error);
      return { error: 'Failed to update permissions' };
    }
  };

  const isChild = familyRole === FAMILY_ROLES.CHILD;
  const isParent = familyRole === FAMILY_ROLES.PARENT;
  const isNanny = familyRole === FAMILY_ROLES.NANNY;
  const isDriver = familyRole === FAMILY_ROLES.DRIVER;
  
  // Permissions based purely on toggles (no role-based fallbacks)
  const canViewAllEvents = permissions?.canViewAllEvents ?? false;
  const canEditEvents = permissions?.canEditEvents ?? false;
  const canCreateEvents = canEditEvents; // Can create if can edit
  const canDeleteEvents = canEditEvents; // Can delete if can edit
  const needsEventFiltering = !canViewAllEvents; // Need filtering if can't view all

  return {
    familyRole,
    familyInfo,
    permissions,
    loading,
    isChild,
    isParent,
    isNanny,
    isDriver,
    canViewAllEvents,
    canEditEvents,
    canCreateEvents,
    canDeleteEvents,
    needsEventFiltering,
    updatePermissions
  };
};